import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Square, Timer } from 'lucide-react';
import { Button, cn } from '@proerp/ui';
import { elapsedSeconds, formatDuration } from '@/features/time-tracking/duration';
import { useStopRunningTimer } from './useStopRunningTimer';

/**
 * Zeiterfassung in der Kopfzeile.
 *
 * Läuft nichts, ist es schlicht der Sprung auf das Zeiterfassungs-Dashboard.
 * Läuft ein Timer, wächst derselbe Knopf nach links zu einer Pille mit der
 * laufenden Zeit und einem Stopp-Knopf — die Zeit ist damit ohne Klick sichtbar
 * und Stoppen bleibt bei einem Klick, so wie vorher in der Karte unten rechts.
 *
 * Am Telefon (unter `tablet`) bleibt es beim grünen Symbol mit Punkt (2026-09-28):
 * Mit Zeit und Stopp-Knopf schob sich die Pille am iPhone — dort meist mit
 * App-Textgröße „Groß" — über Suche und Logo. Zeit und Stoppen stehen einen Tipp
 * entfernt auf dem Dashboard. Bewusst an der Breite, nicht an iOS: ein
 * Android-Telefon gleicher Breite hätte dieselbe Enge.
 *
 * Der Projektname passt nicht in eine 48-px-Leiste und fällt bewusst weg: Er
 * steht auf dem Dashboard, das einen Klick entfernt ist, und in der Praxis läuft
 * der Timer auf dem Projekt, an dem man gerade sitzt.
 *
 * Nur `success-50` und `success-500` sind als Farbtoken definiert (globals.css);
 * Zwischenstufen wie `success-600` fielen still aus. Deckkraft-Varianten dieser
 * beiden statt weiterer Stufen.
 *
 * Echter Anker statt `navigate()` (§9) — Kontextmenü, Cmd-Klick und die
 * Ziel-URL in der Statusleiste bleiben erhalten.
 */
export function TimerPill() {
  const { isAllowed, running, isRunning, stop, isStopping } = useStopRunningTimer();
  const [now, setNow] = useState(() => Date.now());

  // Sekundentakt nur, solange ein Timer läuft.
  useEffect(() => {
    if (!isRunning) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isRunning]);

  if (!isAllowed) return null;

  const seconds = running?.startTime ? elapsedSeconds(running.startTime, now) : 0;

  return (
    <span
      className={cn(
        'flex shrink-0 items-center overflow-hidden rounded-full border',
        'transition-[max-width,background-color,border-color] duration-300 ease-out motion-reduce:transition-none',
        // Ohne laufenden Timer genau so breit wie jeder andere Knopf der Reihe —
        // die Pille wächst aus dem Symbol heraus, statt daneben aufzutauchen.
        isRunning
          ? 'max-w-8 border-transparent tablet:max-w-64 tablet:border-success-500/30 tablet:bg-success-50'
          : 'max-w-8 border-transparent',
      )}
    >
      <Button variant="ghost" size="icon" className="size-8 shrink-0 rounded-full" asChild>
        <Link
          to="/zeiterfassung"
          aria-label={isRunning ? `Zeiterfassung — Timer läuft seit ${formatDuration(seconds)}` : 'Zeiterfassung'}
          title="Zeiterfassung"
          className={cn('relative', isRunning && 'text-success-500 hover:text-success-500')}
        >
          <Timer strokeWidth={1.5} className={cn(isRunning && 'animate-pulse motion-reduce:animate-none')} />
          {isRunning && (
            <span
              aria-hidden
              data-testid="timer-running-dot"
              className="absolute right-1.5 top-1.5 size-2 rounded-full bg-success-500 ring-2 ring-background tablet:hidden"
            />
          )}
        </Link>
      </Button>

      {isRunning && (
        <>
          {/* Die Vorlesehilfe hört die Dauer schon am Link oben. */}
          <span aria-hidden className="hidden shrink-0 pr-1 text-xs font-semibold tabular-nums text-success-500 tablet:inline">
            {formatDuration(seconds)}
          </span>
          <button
            type="button"
            onClick={stop}
            disabled={isStopping}
            aria-label="Timer stoppen"
            title="Timer stoppen"
            className={cn(
              // Touch (iPad): 36 px statt 24 px — wie der Sidebar-Umschalter daneben.
              // `min-h-0`, sonst zieht die globale 44-px-Mindesthöhe den Kreis zur
              // Kapsel (24 × 44 px, Review 2026-09-28).
              'mr-0.5 hidden size-6 shrink-0 items-center justify-center rounded-full tablet:flex touch:size-9 touch:min-h-0',
              'bg-success-500/15 text-success-500 transition-colors',
              'hover:bg-danger-500/20 hover:text-danger-500',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:opacity-50',
            )}
          >
            <Square className="size-2.5 fill-current" strokeWidth={0} aria-hidden />
          </button>
        </>
      )}
    </span>
  );
}
